<!-- Dynamic Property Editor - automatically selects the right input component -->
<script lang="ts">
  import type { PropertySchema } from '../metadata/schemas';
  import { updateObject } from '../stores/sceneEditorStore';

  // Input components
  import NumberInput from './inputs/NumberInput.svelte';
  import StringInput from './inputs/StringInput.svelte';
  import BooleanInput from './inputs/BooleanInput.svelte';
  import SliderInput from './inputs/SliderInput.svelte';
  import ColorInput from './inputs/ColorInput.svelte';
  import SelectInput from './inputs/SelectInput.svelte';
  import TextureInput from './inputs/TextureInput.svelte';

  // Self-import for recursion (Svelte 5 way)
  import PropertyEditor from './PropertyEditor.svelte';

  let {
    objectId,
    object,
    property,
  }: {
    objectId: string;
    object: any;
    property: PropertySchema;
  } = $props();

  const path = property.path || property.key;

  // Handle property update
  function handleChange(value: any, textureName?: string | null) {
    const updates: any = {};

    if (path.includes('.')) {
      // Nested property (e.g., 'config.starCount' or 'anchor.x')
      const parts = path.split('.');
      const parent = parts[0];
      const child = parts.slice(1).join('.');

      // Get current parent object
      const parentObj = object[parent];

      // Create nested update
      if (child.includes('.')) {
        // Deep nested (e.g., 'config.colors.primary')
        const childParts = child.split('.');
        const newParent = { ...parentObj };
        let current = newParent;
        for (let i = 0; i < childParts.length - 1; i++) {
          current[childParts[i]] = { ...current[childParts[i]] };
          current = current[childParts[i]];
        }
        current[childParts[childParts.length - 1]] = value;
        updates[parent] = newParent;
      } else {
        updates[parent] = { ...parentObj, [child]: value };
      }
    } else {
      // Simple property
      updates[path] = value;
    }

    // Special handling for texture (also update textureName)
    if (property.type === 'texture' && textureName !== undefined) {
      updates.textureName = textureName;
    }

    updateObject(objectId, updates);
  }

  // For nested objects, render children recursively
  const isNested = property.isNested && property.children && property.children.length > 0;
</script>

{#if isNested}
  <!-- Nested object - render as collapsible group -->
  <div class="nested-property">
    <div class="text-xs font-medium text-gray-300 mb-2">{property.label}</div>
    <div class="pl-3 border-l border-gray-700 space-y-2">
      {#each property.children || [] as childProp (childProp.key)}
        <PropertyEditor {objectId} {object} property={childProp} />
      {/each}
    </div>
  </div>
{:else}
  <!-- Regular property - render appropriate input -->
  <div class="property-editor">
    {#if property.type !== 'boolean'}
      <label class="block text-xs text-gray-400 mb-1" for="prop-{objectId}-{property.key}">
        {property.label}
      </label>
    {/if}

    {#if property.type === 'number'}
      <NumberInput
        value={property.value}
        onChange={handleChange}
        min={property.min}
        max={property.max}
      />
    {:else if property.type === 'string'}
      <StringInput value={property.value} onChange={handleChange} />
    {:else if property.type === 'boolean'}
      <BooleanInput value={property.value} onChange={handleChange} label={property.label} />
    {:else if property.type === 'slider'}
      <SliderInput
        value={property.value}
        onChange={handleChange}
        min={property.min}
        max={property.max}
        step={property.step}
        showInput={property.showInput}
      />
    {:else if property.type === 'color'}
      <ColorInput value={property.value} onChange={handleChange} />
    {:else if property.type === 'select'}
      <SelectInput
        value={property.value}
        onChange={handleChange}
        options={property.options || []}
      />
    {:else if property.type === 'texture'}
      <TextureInput
        value={property.value}
        textureName={object.textureName || null}
        onChange={handleChange}
        {objectId}
      />
    {:else}
      <!-- Fallback -->
      <StringInput value={property.value} onChange={handleChange} />
    {/if}
  </div>
{/if}

<style>
  .property-editor {
    margin-bottom: 0.75rem;
  }

  .nested-property {
    margin-bottom: 1rem;
  }
</style>
